import { Component, inject, Input, OnDestroy, SimpleChanges } from '@angular/core';
import { Observable, of, Subject, combineLatest, timer } from 'rxjs';
import { catchError, map, shareReplay, switchMap, takeUntil } from 'rxjs/operators';
import { NavItem } from '../../modules/shared/models/nav-item.model';
import { NavigationService, UserAccountType, UserRole } from '../../modules/services/navigation.service';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { AuthenticationService } from '../../modules/services/authentication.service';
import { MetricsService } from '../../modules/services/metrics.service';
import { SettingsService } from '../../modules/services/settings/settings.service';
import { MdaDetailsResponse } from '../../modules/shared/models/mda.details';

@Component({
  selector: 'app-general-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './general-sidebar.component.html',
  styleUrl: './general-sidebar.component.scss'
})
export class GeneralSidebarComponent implements OnDestroy {
  
  @Input() role: any = '';
  @Input() accountType: any = '';
  @Input() brandTitle = 'VMS';
  @Input() brandSubtitle = 'Vendor Management System';

  menuItems$!: Observable<NavItem[]>;
  collapsed = false;
  dropdownItemIndex: number | null = null;

  logo: string | null = null;

  private navigationService = inject(NavigationService);
  private metricsService = inject(MetricsService);
  private readonly destroy$ = new Subject<void>();

  authService:AuthenticationService = inject(AuthenticationService);

  constructor(
    private readonly settingsService: SettingsService
  ){}
  ngOnInit(): void {
    this.collapsed = localStorage.getItem('app-sidebar-collapsed') === 'true';
    this.role = this.authService.getUserRoles()[0];
    this.accountType = this.authService.getAccountType();
    this.loadMenu();


    this.settingsService.mdaDetails.subscribe({
      next: (data: MdaDetailsResponse) => {
        if (data) {
          this.logo = data.logo ? data.logo : null;
        }
      }
    })
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['role'] && !changes['role'].firstChange) {
      this.loadMenu();
    }
  }

  toggleCollapse(): void {
    this.collapsed = !this.collapsed;
    localStorage.setItem('app-sidebar-collapsed', String(this.collapsed));
  }

  trackById(_: number, item: NavItem): string {
    return item.id;
  }

  private loadMenu(): void {
    // const baseMenu$ = this.navigationService.getMenuByRole(this.role as UserRole);
    const baseMenu$ = this.navigationService.getMenuByAccountType(this.accountType as UserAccountType);

    // const isAdmin = String(this.role ?? '').toUpperCase() === 'ADMIN';
    const isAdmin = String(this.accountType ?? '').toUpperCase() === 'ADMIN';
    const pendingApprovals$ = isAdmin
      ? timer(0, 60_000).pipe(
          switchMap(() => this.metricsService.getPendingWorkflowApprovals()),
          catchError(() => of({ pending: 0, byWorkflow: [] })),
          shareReplay({ bufferSize: 1, refCount: true }),
        )
      : of({ pending: 0, byWorkflow: [] });

    this.menuItems$ = combineLatest([baseMenu$, pendingApprovals$]).pipe(
      map(([items, pending]) => {
        const count = Number.isFinite((pending as any)?.pending) ? Number((pending as any).pending) : 0;
        const byWorkflow = Array.isArray((pending as any)?.byWorkflow) ? (pending as any).byWorkflow : [];
        const vendorPending =
          byWorkflow.find((w: any) => String(w?.workflowName ?? '').toUpperCase() === 'VENDOR_APPLICATION')
            ?.pending ?? 0;

        return (Array.isArray(items) ? items : []).map((item) => {
          if (item.id !== 'approval') return item;
          const children = Array.isArray(item.children)
            ? item.children.map((child) => {
                if (child.id === 'approval-vendors') {
                  const safe = Number.isFinite(Number(vendorPending)) ? Number(vendorPending) : 0;
                  return { ...child, badgeCount: safe > 0 ? safe : undefined };
                }
                return child;
              })
            : item.children;

          return { ...item, badgeCount: count > 0 ? count : undefined, children };
        });
      }),
      takeUntil(this.destroy$),
    );
  }

  onToggleParentItem(itemIndex: number) {
    this.dropdownItemIndex = itemIndex === this.dropdownItemIndex ? null : itemIndex;
  }

  shouldParentItem(value: boolean) {
    if(value) this.dropdownItemIndex = null;
  }
}
